import { GoogleAnalytics } from "~/google_analytics/events";
import { EventParameter } from "~/google_analytics/events.enum";

const remove = async(couponCode: string) => {
  const { transactionId } = useCartSummary();
  const runtimeConfig = useRuntimeConfig();
  const { user, access_token } = useAuth();
  try {
    await useAPIHandler("post", `/payments/wallet/removeAppliedCoupon`, {
      body: {
        partner_id: runtimeConfig.public.partnerCode,
        coupon_code: couponCode,
        ...(access_token && { mobile: user }),
        tab_id: window?.name || null
      },
      auth: true,
    });
    GoogleAnalytics.cta({
      [EventParameter.CTA_NAME]: 'remove_coupon',
      [EventParameter.COUPON_CODE]: couponCode,
      [EventParameter.TRANSACTION_ID]: transactionId.value,
      [EventParameter.COUPON_STATUS]: 'removed'
    });
  } catch (err) {
    console.log(err);
  } 
  useCart().removeOffer();
}

const checkOffer = async (couponCode: string) => {
  const runtimeConfig = useRuntimeConfig();
  const { cartItems, metrics } = useCartSummary();
  const { categoryIds, skuIds, provider_src } = metrics.value;
  const cart = useCart();
  const { user, access_token } = useAuth();
  const abstractItems = cartItems.value.map((item) => ({
    provider: { ukid: item.ukid, ...item.provider },
    seller: { id: item.seller_np.id }
  }));
   if (!window.name) {
        window.name = "tab-" + Math.random().toString(36).substring(2, 12);
      }
  const tab_id = window.name;
  const transactionID = cart.transaction_id;

  try {
    const { data } = await useAPIHandler('post', '/payments/wallet/checkOfferApplicable', {
      body: {
        partner_id: runtimeConfig.public.partnerCode,
        coupon_code: couponCode.toUpperCase(),
        itemDetails: abstractItems,
        categories: categoryIds,
        products: skuIds,
        provider_src: provider_src[0],
        transaction_id: transactionID,
        tab_id: tab_id,
        ...(access_token && { mobile: user }),
      },
    });

    const { statusCode, data: couponData, message } = data;
    if (!statusCode) {
      if(cart.offers.offer_code){
        await remove(couponCode);
      }
      return { status: false, error: message };
    }
    return { status: true, data: couponData };
  } catch (error: any) {
    return { status: false, error: error?.message };
  }
}

const calculateCouponAmount = (coupon: any, billDetails: any, amountToPay: number) => {
  const cart = useCart();
  const { offer_type, offer_value, scope, max_limit = 0, minimum_item_value } = coupon;
  const isScoped = ['subtotal', 'delivery'].includes(scope.toLowerCase());
  const responseData = cart.response?.data;
  const allSummary = responseData.flatMap((k: any) => k.summary || []);


  const deliveryItem = billDetails.value.find((k: any) => k.id === 'delivery');
  const deliveryAmount = deliveryItem ? Number(deliveryItem.value) : 0;
  
  const baseAmount = isScoped
    ? scope?.toLowerCase() === 'delivery'
        ? deliveryAmount
        : allSummary
            .filter((k: any) => k.type === 'item' && Number(k.total) >= Number(minimum_item_value))
            .reduce((acc: number, curr: any) => acc + Number(curr.total), 0)
    : amountToPay;
    
  let discount = 0;
  if (offer_type === 'percentage') {
    discount = (baseAmount * parseFloat(offer_value)) / 100;
  } else if (offer_type === 'value') {
    discount = parseFloat(offer_value);
  }
  const couponAmount = Number(max_limit) > 0 ? Math.min(discount, Number(max_limit)) : discount;
  if (scope.toLowerCase() === 'delivery') return Math.max(0, Math.min(couponAmount, deliveryAmount));
  return couponAmount;
} 

export const useCouponMaster = () => {
  const coupons = ref([]);
  const error = ref<string | null>(null);
  const isLoading = ref(false);
  const isChecking = ref(false);
  const analyticsEvents = useAnalyticsEvents();
  const { access_token } = storeToRefs(useAuth());
  const cart = useCart();
  
  const fetch = async () => {
    const runtimeConfig = useRuntimeConfig();
    const { user } = useAuth();
    const { transactionId, metrics } = useCartSummary();
    const { ukIds, sellerIds, categoryIds, skuIds, provider_src } = metrics.value;
    isLoading.value = true;

    try {
      const { data } = await useAPIHandler('post', '/offers/list', {
        body: {
          partner_id: runtimeConfig.public.partnerCode,
          seller_id: sellerIds,
          provider_ukid: ukIds,
          categories: categoryIds,
          provider_src: provider_src[0],
          transaction_id: transactionId.value,
          sku_ids: skuIds,
          ...(user && user.phone ? { mobile: user.phone } : {}),
          delivery_charges: true,
        },
      });
      
      coupons.value = data?.sort((a: any, b: any) => {
        const seqA = a.sequence_number ?? Number.MAX_SAFE_INTEGER;
        const seqB = b.sequence_number ?? Number.MAX_SAFE_INTEGER;
        return Number(seqA) - Number(seqB);
      });
    } catch (err: any) {
      console.error('Error fetching coupons:', err);
      coupons.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  const apply = async (couponCode: string, track = true) => {
    const { amountToPay, billDetails, hasError, transactionId } = useCartSummary();
    if (amountToPay.value <= 0 || hasError.value) {
      useCartToast({
        description: 'Please correct any cart errors to apply a coupon',
        timeout: 3000
      });
      GoogleAnalytics.cta({
        [EventParameter.CTA_NAME]: 'apply_coupon',
        [EventParameter.COUPON_CODE]: couponCode.trim().toUpperCase(),
        [EventParameter.TRANSACTION_ID]: transactionId.value,
        [EventParameter.COUPON_STATUS]: 'failure'
      });
      return { status: false };
    }

    const defaultError = 'Invalid coupon code. Please try again.';
    error.value = null;
    isChecking.value = true;
    
    const { status, data, error: couponError } = await checkOffer(couponCode);
    if (!status) {
      error.value = couponError || defaultError;
      isChecking.value = false;
      useCartToast({
        description: couponError,
        timeout: 3000
      });
      GoogleAnalytics.cta({
      [EventParameter.CTA_NAME]: 'apply_coupon',
      [EventParameter.COUPON_CODE]: couponCode.trim().toUpperCase(),
      [EventParameter.TRANSACTION_ID]: transactionId.value,
      [EventParameter.COUPON_STATUS]: status ? 'success' : 'failure'
    });
      return { status: false };
    }
    const cartTotalBeforeDiscount = amountToPay.value;
    const couponAmount = calculateCouponAmount(data, billDetails, cartTotalBeforeDiscount);
    useCart().addOffer({
      offer_code: couponCode,
      offer_type: data.offer_type,
      offer_scope: data.offer_scope || 'subtotal',
      offer_amount: parseFloat(couponAmount.toFixed(2)),
      total_amount: parseFloat((cartTotalBeforeDiscount - couponAmount).toFixed(2)),
      offer_provider_split: [],
    });
    if (track) {
      useCartTracker().trackEvent('cta_event', {
        event: 'click', cta_name: 'Apply coupons', coupon_code: couponCode
      });
      useAnalytics(analyticsEvents.COUPON_APPLIED, {
        coupon_code: couponCode.trim().toUpperCase(),
        cart_total_value: cartTotalBeforeDiscount,
        discount_amount: parseFloat(couponAmount.toFixed(2))
      });
    }
    GoogleAnalytics.cta({
      [EventParameter.CTA_NAME]: 'apply_coupon',
      [EventParameter.COUPON_CODE]: couponCode.trim().toUpperCase(),
      [EventParameter.TRANSACTION_ID]: transactionId.value,
      [EventParameter.COUPON_STATUS]: status ? 'success' : 'failure'
    });
    isChecking.value = false;
    return { status: true };
  }
  watch(
    access_token,
    async (token) => {
      if (!token && cart.offers?.offer_code) {
        await remove(cart.offers.offer_code);
      }
    },
    { immediate: true }
  );
  return { coupons, error, isLoading, isChecking, fetch, apply, remove, checkOffer };
};
